import { Avatar, CollapseCard, Tooltip } from '@dixa/lib-shared-habla';
import { useRef } from 'react';

import { UseOrderedDragDropOptions } from 'utils/useOrderedDragDrop';

import { CardMoveHandle } from '../../components/cardMoveHandle/cardMoveHandle';

const OriginatingUrl = ({ url }: { url: string }) => (
  <Tooltip content={url}>
    <a target="_blank" rel="noopener noreferrer" href={url}>
      {url}
    </a>
  </Tooltip>
);

const OriginatingCountryCode = ({ countryCode }: { countryCode: string }) => (
  <span>{countryCode}</span>
);

type UserTrackingCardProps = Pick<
  UseOrderedDragDropOptions,
  'type' | 'index' | 'onMove'
> & {
  isOpen: boolean;
  isMinimized?: boolean;
  attributes: Record<string, string>;
  onToggle: (isOpen: boolean) => void;
};

export const UserTrackingCard = ({
  isOpen,
  isMinimized = false,
  attributes,
  onToggle,
  index,
  type,
  onMove,
}: UserTrackingCardProps): JSX.Element | null => {
  const row = useRef<HTMLDivElement>(null);

  const data = attributes ?? {};

  const items: Array<[string, string | JSX.Element]> = [];
  if (data.ip_address) {
    items.push(['IP Address', data.ip_address]);
  }
  if (data.originating_country_code) {
    items.push([
      'Country',
      <OriginatingCountryCode
        key={data.originating_country_code}
        countryCode={data.originating_country_code}
      />,
    ]);
  }
  if (data.originating_region) {
    items.push(['Region', data.originating_region]);
  }
  if (data.originating_city) {
    items.push(['City', data.originating_city]);
  }
  if (data.originating_url) {
    items.push([
      'Origin Url',
      <OriginatingUrl key={data.originating_url} url={data.originating_url} />,
    ]);
  }
  if (data.os_name) {
    items.push(['OS', data.os_name]);
  }
  if (data.browser_name) {
    items.push(['Browser Name', data.browser_name]);
  }
  if (data.browser_version) {
    items.push(['Browser Version', data.browser_version]);
  }

  if (items.length === 0) {
    // If there aren't any attributes to show, then don't render this card altogether
    return null;
  }

  const avatar = <Avatar icon="map-pin" />;

  if (isMinimized) {
    return <div>{avatar}</div>;
  }

  const lines = items.map(([label, value]) => (
    <CollapseCard.Line key={label} label={label}>
      {value}
    </CollapseCard.Line>
  ));

  return (
    <CollapseCard containerRef={row} isOpen={isOpen} onToggle={onToggle}>
      <CollapseCard.Header
        avatar={avatar}
        title="Tracking"
        slotBeforeTitle={
          <CardMoveHandle
            index={index}
            target={row}
            type={type}
            onMove={onMove}
          />
        }
      >
        Tracking
      </CollapseCard.Header>
      <CollapseCard.Content>{lines}</CollapseCard.Content>
    </CollapseCard>
  );
};
